import React, {
	createContext,
	type PropsWithChildren,
	useContext,
	useOptimistic,
	useState,
} from 'react';
import type { TGroup } from '../pages/overview';
import optimisticReducer, {
	type OptimisticAction,
} from '../reducer/optimistic-reducer';

type GroupContextType = PropsWithChildren & {
	groups: TGroup[];
	setGroups: React.Dispatch<React.SetStateAction<TGroup[]>>;
	optimisticGroups: TGroup[];
	setOptimisticGroups: (action: OptimisticAction) => void;
};

const GroupContext = createContext<GroupContextType | undefined>(undefined);

export const GroupProvider: React.FC<
	PropsWithChildren & { initialGroups: TGroup[] }
> = ({ initialGroups, children }) => {
	const [groups, setGroups] = useState<TGroup[]>(initialGroups);
	const [optimisticGroups, setOptimisticGroups] = useOptimistic(
		groups,
		optimisticReducer,
	);

	return (
		<GroupContext.Provider
			value={{ groups, setGroups, optimisticGroups, setOptimisticGroups }}
		>
			{children}
		</GroupContext.Provider>
	);
};

export const useGroupContext = () => {
	const context = useContext(GroupContext);
	if (context === undefined) {
		throw new Error('useGroupContext must be used within a GroupProvider');
	}
	return context;
};
